import { useCallback, useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profileRole, setProfileRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);

  const loadProfileRole = useCallback(async (current: User | null) => {
    if (!current) {
      setProfileRole(null);
      return null;
    }

    const { data } = await supabase
      .from("profiles")
      .select("role")
      .eq("id", current.id)
      .maybeSingle();

    const role = data?.role ?? null;
    setProfileRole(role);
    return role;
  }, []);

  const refresh = useCallback(async () => {
    const {
      data: { user: current },
    } = await supabase.auth.getUser();

    setUser(current);
    await loadProfileRole(current);
    return current;
  }, [loadProfileRole]);

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (active) {
          const current = session?.user ?? null;
          setUser(current);
          await loadProfileRole(current);
        }
      } catch (reason) {
        if (active) {
          setError(reason);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;

      const current = session?.user ?? null;
      setUser(current);

      // O callback do Supabase Auth não deve executar outra chamada ao Supabase
      // de forma síncrona. O carregamento do perfil fica para o próximo tick,
      // evitando a falha intermitente durante o retorno do Google OAuth.
      window.setTimeout(() => {
        if (!active) return;
        void loadProfileRole(current);
      }, 0);

      if (event === "PASSWORD_RECOVERY") {
        setRequiresPasswordReset(true);
      }
    });

    void loadSession();

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [loadProfileRole]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);

    const { data, error: signInError } =
      await supabase.auth.signInWithPassword({
        email,
        password,
      });

    if (signInError) {
      throw signInError;
    }

    setUser(data.user);
    await loadProfileRole(data.user);
  }, [loadProfileRole]);

  const signUp = useCallback(
    async (email: string, password: string, name: string) => {
      setError(null);

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name || undefined,
          },
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      setUser(data.user);
      await loadProfileRole(data.user);

      return Boolean(data.session);
    },
    [loadProfileRole],
  );

  const signInWithGoogle = useCallback(async () => {
    setError(null);

    const { data, error: googleError } =
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

    if (googleError) {
      throw googleError;
    }

    if (!data.url) {
      throw new Error(
        "O Supabase não retornou a URL de autenticação do Google.",
      );
    }

    window.location.assign(data.url);
  }, []);

  const recover = useCallback(async (email: string) => {
    setError(null);

    const { error: recoveryError } =
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/`,
      });

    if (recoveryError) {
      throw recoveryError;
    }
  }, []);

  const completePasswordRecovery = useCallback(
    async (password: string) => {
      setError(null);

      const { data, error: updateError } =
        await supabase.auth.updateUser({
          password,
        });

      if (updateError) {
        throw updateError;
      }

      setUser(data.user);
      await loadProfileRole(data.user);
      setRequiresPasswordReset(false);
    },
    [loadProfileRole],
  );

  const logout = useCallback(async () => {
    setError(null);

    const { error: logoutError } = await supabase.auth.signOut();

    if (logoutError) {
      throw logoutError;
    }

    setUser(null);
    setProfileRole(null);
  }, []);

  return {
    user,
    profileRole,
    loading,
    error,
    isAuthenticated: Boolean(user),
    requiresPasswordReset,
    refresh,
    login: signIn,
    signup: signUp,
    signInWithGoogle,
    recover,
    completePasswordRecovery,
    logout,
  };
}
